import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useField, useFormikContext } from 'formik'
import { REQUIREMENT_TYPE_ENUM, selectEditingCampaignCountry } from '../../../../state/campaigns'
import { requestProfileOptions, selectCityTypesByEditingCampaignCountry } from 'state/constants'
import _ from 'lodash'
import { PLATFORM_ENUM } from 'services/api/constants'
import LocationRequirement, { LocationData } from './LocationRequirement'
import OccupationRequirement from './OccupationRequirement'
import AgeRequirement from './AgeRequirement'
import FollowerRequirement from './FollowerRequirement'
import GenderRequirement from './GenderRequirement'
import BlockedCampaigns from './BlockedCampaigns'
import Icon from 'components/Icon/Icon'
import { RequirementContainer, StyledRichContentMenu } from './styles'
import { InputFieldContainer } from 'components/Shared/Fields/styles'
import FreeInputRequirement from './FreeInputRequirement'
import CampaignEditorInputFieldContainer from 'components/Shared/Permission/CampaignEditorInputFieldContainer'
import RectangleButton from 'particles/Button/RectangleButton'
import MenuItem from 'particles/MenuItem'
import { generateUuid } from 'services/uuid'
import FieldTitleAndSubTitle from 'components/Shared/Fields/FieldTitleAndSubTitle'
import { fieldIdentifiers } from 'components/Shared/Comment'
import ProfileTypeRequirement from './ProfileTypeRequirement'

enum REQUIREMENT_TYPE_ENUM_CLIENT {
    AGE = 1,
    GENDER,
    IG_FOLLOWERS,
    FB_FRIENDS,
    TIKTOK_FOLLOWERS,
    TWITTER_FOLLOWERS,
    LOCATION,
    OCCUPATION,
    BLOCKED_CAMPAIGNS,
    OTHER,
    PROFILE_TYPE,
}

interface RequirementData<T, P> {
    type: T
    properties: P
    uuid?: string
}

type AgeRequirementData = RequirementData<
    REQUIREMENT_TYPE_ENUM.AGE,
    {
        min: number | null
        max: number | null
    }
>
type GenderRequirementData = RequirementData<
    REQUIREMENT_TYPE_ENUM.GENDER,
    {
        value: string | null
    }
>
export type FollowerRequirementData = RequirementData<
    REQUIREMENT_TYPE_ENUM.FOLLOWER,
    {
        followers: {
            uuid: string
            platform: number
            min: number | null
            max: number | null
        }
    }
>
type OccupationRequirementData = RequirementData<
    REQUIREMENT_TYPE_ENUM.OCCUPATION,
    {
        occupations: {
            value: number
        }[]
    }
>

type LocationRequirementData = RequirementData<
    REQUIREMENT_TYPE_ENUM.LOCATION,
    {
        locations: LocationData[]
    }
>
type ProfileTypeRequirementData = RequirementData<
    REQUIREMENT_TYPE_ENUM.PROFILE_TYPE,
    {
        profile_types: { value: string }[]
    }
>
type FreeInputRequirementData = RequirementData<
    REQUIREMENT_TYPE_ENUM.OTHERS,
    {
        value?: string | null
    }
>
export const BLOCKED_CAMPAIGNS_TYPE = 'BLOCKED_CAMPAIGNS'
export type BlockedCampaignsData = RequirementData<
    typeof BLOCKED_CAMPAIGNS_TYPE,
    { inputValue?: string | null; uuids?: string[] | null }
>

export type Requirement =
    | AgeRequirementData
    | GenderRequirementData
    | FollowerRequirementData
    | OccupationRequirementData
    | LocationRequirementData
    | BlockedCampaignsData
    | ProfileTypeRequirementData
    | FreeInputRequirementData

interface RequirementProps {
    type: REQUIREMENT_TYPE_ENUM_CLIENT
    category: string
    label: string
    description: string
    initialValue: Requirement
}
const CampaignRequirements: React.FC<{ name: string }> = ({ name }) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const [t] = useTranslation()
    const dispatch = useDispatch()
    const [field] = useField<any>(name)
    const { setFieldValue, values } = useFormikContext<any>()
    const editingCampaignCountry = useSelector(selectEditingCampaignCountry)
    const cityTypes = useSelector(selectCityTypesByEditingCampaignCountry)
    const rank1CityType = cityTypes.find(city => city.rank === 1)
    const rank2CityType = cityTypes.find(city => city.rank === 2)

    useEffect(() => {
        if (editingCampaignCountry) dispatch(requestProfileOptions({ country: editingCampaignCountry }))
    }, [editingCampaignCountry])

    const initialLocation = useMemo(() => {
        let location = {}
        if (rank1CityType && rank2CityType) {
            location = {
                [rank1CityType.id_name]: {
                    id: null,
                    name: null,
                    uuid: generateUuid(),
                },
                [rank2CityType.id_name]: {
                    id: null,
                    name: null,
                    uuid: generateUuid(),
                },
            }
        } else if (rank1CityType && !rank2CityType) {
            location = {
                [rank1CityType.id_name]: {
                    id: null,
                    name: null,
                    uuid: generateUuid(),
                },
            }
        }
        return location
    }, [])

    const requirementTypes: RequirementProps[] = [
        {
            type: REQUIREMENT_TYPE_ENUM_CLIENT.AGE,
            category: 'personal',
            label: t('campaignEditor.requirement.ageHeader'),
            description: t('campaignEditor.requirement.ageMenuDescription'),
            initialValue: useMemo(() => {
                return {
                    uuid: generateUuid(),
                    type: REQUIREMENT_TYPE_ENUM.AGE,
                    properties: {
                        min: null,
                        max: null,
                    },
                }
            }, []),
        },
        {
            type: REQUIREMENT_TYPE_ENUM_CLIENT.GENDER,
            category: 'personal',
            label: t('campaignEditor.requirement.genderHeader'),
            description: t('campaignEditor.requirement.genderMenuDescription'),
            initialValue: useMemo(() => {
                return { uuid: generateUuid(), type: REQUIREMENT_TYPE_ENUM.GENDER, properties: { value: null } }
            }, []),
        },
        {
            type: REQUIREMENT_TYPE_ENUM_CLIENT.IG_FOLLOWERS,
            category: 'social-media',
            label: t(`socialMedia.requirement.instagramFollowers`),
            description: t('campaignEditor.requirement.instagramFollowersMenuDescription'),
            initialValue: useMemo(() => {
                return {
                    type: REQUIREMENT_TYPE_ENUM.FOLLOWER,
                    properties: {
                        followers: { uuid: generateUuid(), platform: PLATFORM_ENUM.IG, min: null, max: null },
                    },
                }
            }, []),
        },
        {
            type: REQUIREMENT_TYPE_ENUM_CLIENT.FB_FRIENDS,
            category: 'social-media',
            label: t('socialMedia.requirement.facebookFriends'),
            description: t('campaignEditor.requirement.facebookFriendsMenuDescription'),
            initialValue: useMemo(() => {
                return {
                    type: REQUIREMENT_TYPE_ENUM.FOLLOWER,
                    properties: {
                        followers: { uuid: generateUuid(), platform: PLATFORM_ENUM.FB, min: null, max: null },
                    },
                }
            }, []),
        },
        {
            type: REQUIREMENT_TYPE_ENUM_CLIENT.TWITTER_FOLLOWERS,
            category: 'social-media',
            label: t('socialMedia.requirement.twitterFollowers'),
            description: t('campaignEditor.requirement.twitterFollowersMenuDescription'),
            initialValue: useMemo(() => {
                return {
                    type: REQUIREMENT_TYPE_ENUM.FOLLOWER,
                    properties: {
                        followers: { uuid: generateUuid(), platform: PLATFORM_ENUM.TWITTER, min: null, max: null },
                    },
                }
            }, []),
        },
        {
            type: REQUIREMENT_TYPE_ENUM_CLIENT.TIKTOK_FOLLOWERS,
            category: 'social-media',
            label: t('socialMedia.requirement.tiktokFollowers'),
            description: t('campaignEditor.requirement.tiktokFollowersMenuDescription'),
            initialValue: useMemo(() => {
                return {
                    type: REQUIREMENT_TYPE_ENUM.FOLLOWER,
                    properties: {
                        followers: { uuid: generateUuid(), platform: PLATFORM_ENUM.TIKTOK, min: null, max: null },
                    },
                }
            }, []),
        },

        {
            type: REQUIREMENT_TYPE_ENUM_CLIENT.OCCUPATION,
            category: 'personal',
            label: t('campaignEditor.requirement.employmentStatusHeader'),
            description: t('campaignEditor.requirement.employmentStatusMenuDescription'),
            initialValue: useMemo(() => {
                return {
                    uuid: generateUuid(),
                    type: REQUIREMENT_TYPE_ENUM.OCCUPATION,
                    properties: {
                        occupations: [],
                    },
                }
            }, []),
        },
        {
            type: REQUIREMENT_TYPE_ENUM_CLIENT.LOCATION,
            category: 'personal',
            label: t('campaignEditor.requirement.locationHeader'),
            description: t('campaignEditor.requirement.locationMenuDescription'),
            initialValue: useMemo(() => {
                return {
                    type: REQUIREMENT_TYPE_ENUM.LOCATION,
                    properties: {
                        locations: [initialLocation],
                    },
                }
            }, []),
        },
        {
            type: REQUIREMENT_TYPE_ENUM_CLIENT.BLOCKED_CAMPAIGNS,
            category: 'campaign-conflict',
            label: t('campaignEditor.requirement.conflictingCampaignsHeader'),
            description: t('campaignEditor.requirement.conflictingCampaignsMenuDescription'),
            initialValue: useMemo(() => {
                return {
                    uuid: generateUuid(),
                    type: BLOCKED_CAMPAIGNS_TYPE,
                    properties: {
                        inputValue: undefined,
                        uuids: null,
                    },
                }
            }, []),
        },
        {
            type: REQUIREMENT_TYPE_ENUM_CLIENT.PROFILE_TYPE,
            category: 'other-requirement',
            label: t('campaignEditor.requirement.profileTypeSectionMenuHeader'),
            description: t('campaignEditor.requirement.profileTypeMenuDescription'),
            initialValue: {
                uuid: generateUuid(),
                type: REQUIREMENT_TYPE_ENUM.PROFILE_TYPE,
                properties: {
                    profile_types: [],
                },
            },
        },
        {
            type: REQUIREMENT_TYPE_ENUM_CLIENT.OTHER,
            category: 'other-requirement',
            label: t('campaignEditor.requirement.freeInputHeader'),
            description: t('campaignEditor.requirement.freeInputMenuDescription'),
            initialValue: {
                uuid: generateUuid(),
                type: REQUIREMENT_TYPE_ENUM.OTHERS,
                properties: {
                    value: null,
                },
            },
        },
    ]

    const requirementList = useMemo(() => {
        const personalList = requirementTypes.filter(requirement => {
            return requirement.category === 'personal'
        })
        const socialMediaList = requirementTypes.filter(requirement => {
            return requirement.category === 'social-media'
        })
        const campaignConflictList = requirementTypes.filter(requirement => {
            return requirement.category === 'campaign-conflict'
        })
        const otherList = requirementTypes.filter(requirement => {
            return requirement.category === 'other-requirement'
        })
        const menuList = [
            {
                title: t('campaignEditor.requirement.partiposterRequirementMenuHeader'),
                requirements: personalList,
            },
            {
                title: t('campaignEditor.requirement.socialMediaRequirementMenuHeader'),
                requirements: socialMediaList,
            },
            {
                title: t('campaignEditor.requirement.campaignConflictSectionMenuHeader'),
                requirements: campaignConflictList,
            },
            {
                title: t('campaignEditor.requirement.otherRequirementMenuHeader'),
                requirements: otherList,
            },
        ]
        return menuList
    }, [requirementTypes])

    const handleAddBlock = (initialValue: Requirement): void => {
        if (
            initialValue.type !== REQUIREMENT_TYPE_ENUM.FOLLOWER &&
            field.value.findIndex((requirement: Requirement) => requirement.type === initialValue.type) > -1
        )
            return
        if (
            initialValue.type === REQUIREMENT_TYPE_ENUM.FOLLOWER &&
            field.value.findIndex(
                (requirement: Requirement) =>
                    requirement.properties.hasOwnProperty('followers') &&
                    (requirement as FollowerRequirementData).properties.followers.platform ===
                        initialValue.properties.followers.platform,
            ) > -1
        )
            return

        const next = field.value.concat([initialValue])
        setAnchorEl(null)
        setFieldValue(field.name, next)
    }

    const handleRemoveBlock = (index: number): void => {
        const next = [...field.value]
        next.splice(index, 1)
        setFieldValue(field.name, next)
    }

    const getRequirementType = (requirement: Requirement): REQUIREMENT_TYPE_ENUM_CLIENT | undefined => {
        if (requirement.type === REQUIREMENT_TYPE_ENUM.FOLLOWER) {
            switch (requirement.properties.followers.platform) {
                case PLATFORM_ENUM.IG:
                    return REQUIREMENT_TYPE_ENUM_CLIENT.IG_FOLLOWERS
                case PLATFORM_ENUM.FB:
                    return REQUIREMENT_TYPE_ENUM_CLIENT.FB_FRIENDS
                case PLATFORM_ENUM.TIKTOK:
                    return REQUIREMENT_TYPE_ENUM_CLIENT.TIKTOK_FOLLOWERS
                case PLATFORM_ENUM.TWITTER:
                    return REQUIREMENT_TYPE_ENUM_CLIENT.TWITTER_FOLLOWERS
                default:
                    return
            }
        }
        const target = requirementTypes.find(requirementType => requirement.type === requirementType.initialValue.type)
        if (target) return target.type
        return
    }

    const getBlockContent = (index: number, requirement: Requirement, onRemove: () => void): JSX.Element | null => {
        const requirementType = getRequirementType(requirement)
        if (!requirementType) return null
        switch (requirementType) {
            case REQUIREMENT_TYPE_ENUM_CLIENT.AGE:
                return (
                    <AgeRequirement
                        requirementUuid={requirement?.uuid}
                        name={`requirementsData[${index}].properties`}
                        onRemove={onRemove}
                    />
                )
            case REQUIREMENT_TYPE_ENUM_CLIENT.GENDER:
                return (
                    <GenderRequirement
                        name={`requirementsData[${index}].properties.value`}
                        onRemove={onRemove}
                        requirementUuid={requirement?.uuid}
                    />
                )
            case REQUIREMENT_TYPE_ENUM_CLIENT.OCCUPATION:
                return (
                    <OccupationRequirement
                        name={`requirementsData[${index}].properties.occupations`}
                        onRemove={onRemove}
                        requirementUuid={requirement?.uuid}
                    />
                )
            case REQUIREMENT_TYPE_ENUM_CLIENT.LOCATION:
                return (
                    <LocationRequirement name={`requirementsData[${index}].properties.locations`} onRemove={onRemove} />
                )
            case REQUIREMENT_TYPE_ENUM_CLIENT.IG_FOLLOWERS:
                return (
                    <FollowerRequirement
                        name={`requirementsData[${index}].properties.followers`}
                        platform={PLATFORM_ENUM.IG}
                        onRemove={onRemove}
                    />
                )
            case REQUIREMENT_TYPE_ENUM_CLIENT.FB_FRIENDS:
                return (
                    <FollowerRequirement
                        name={`requirementsData[${index}].properties.followers`}
                        platform={PLATFORM_ENUM.FB}
                        onRemove={onRemove}
                    />
                )
            case REQUIREMENT_TYPE_ENUM_CLIENT.TIKTOK_FOLLOWERS:
                return (
                    <FollowerRequirement
                        name={`requirementsData[${index}].properties.followers`}
                        platform={PLATFORM_ENUM.TIKTOK}
                        onRemove={onRemove}
                    />
                )
            case REQUIREMENT_TYPE_ENUM_CLIENT.TWITTER_FOLLOWERS:
                return (
                    <FollowerRequirement
                        name={`requirementsData[${index}].properties.followers`}
                        platform={PLATFORM_ENUM.TWITTER}
                        onRemove={onRemove}
                    />
                )
            case REQUIREMENT_TYPE_ENUM_CLIENT.BLOCKED_CAMPAIGNS:
                return (
                    <BlockedCampaigns
                        requirementUuid={requirement?.uuid}
                        name={`requirementsData[${index}].properties`}
                        onRemove={onRemove}
                    />
                )
            case REQUIREMENT_TYPE_ENUM_CLIENT.PROFILE_TYPE:
                return (
                    <ProfileTypeRequirement
                        requirementUuid={requirement?.uuid}
                        name={`requirementsData[${index}].properties.profile_types`}
                        onRemove={onRemove}
                    />
                )
            case REQUIREMENT_TYPE_ENUM_CLIENT.OTHER:
                return (
                    <FreeInputRequirement
                        requirementUuid={requirement?.uuid}
                        name={`requirementsData[${index}].properties.value`}
                        onRemove={onRemove}
                    />
                )
        }
    }
    const getRequirementIcon = (requirementType: REQUIREMENT_TYPE_ENUM_CLIENT): JSX.Element => {
        switch (requirementType) {
            case REQUIREMENT_TYPE_ENUM_CLIENT.AGE:
                return <Icon.AgeIcon />
            case REQUIREMENT_TYPE_ENUM_CLIENT.GENDER:
                return <Icon.GenderIcon />
            case REQUIREMENT_TYPE_ENUM_CLIENT.OCCUPATION:
                return <Icon.OccupationIcon />
            case REQUIREMENT_TYPE_ENUM_CLIENT.LOCATION:
                return <Icon.LocationIcon />
            case REQUIREMENT_TYPE_ENUM_CLIENT.IG_FOLLOWERS:
                return <Icon.InstagramRequirementIcon />
            case REQUIREMENT_TYPE_ENUM_CLIENT.FB_FRIENDS:
                return <Icon.FacebookRequirementIcon />
            case REQUIREMENT_TYPE_ENUM_CLIENT.TIKTOK_FOLLOWERS:
                return <Icon.TiktokIcon />
            case REQUIREMENT_TYPE_ENUM_CLIENT.TWITTER_FOLLOWERS:
                return <Icon.TwitterIcon />
            case REQUIREMENT_TYPE_ENUM_CLIENT.BLOCKED_CAMPAIGNS:
                return <Icon.CampaignConflictIcon />
            case REQUIREMENT_TYPE_ENUM_CLIENT.PROFILE_TYPE:
                return <Icon.ProfileTypeIcon />
            case REQUIREMENT_TYPE_ENUM_CLIENT.OTHER:
                return <Icon.FreeInputIcon />
            default:
                return <></>
        }
    }
    return (
        <InputFieldContainer>
            <FieldTitleAndSubTitle
                title={t('campaignEditor.completeSetup.enterCampaignRequirementHeader')}
                subtitle={t('campaignEditor.completeSetup.selectCampaignRequirementSubtext')}
                identifier={fieldIdentifiers.campaignRequirement}
            />
            {values.requirementsData.map((requirement: Requirement, index: number) => (
                <CampaignEditorInputFieldContainer key={index}>
                    {getBlockContent(index, requirement, () => handleRemoveBlock(index))}
                </CampaignEditorInputFieldContainer>
            ))}
            <RequirementContainer id="requirement-list">
                <CampaignEditorInputFieldContainer>
                    {(isReadOnly: boolean) =>
                        !isReadOnly && (
                            <RectangleButton
                                form="outlined"
                                size="s"
                                label={t('button.addRequirement')}
                                icon="plus"
                                onClick={(e: React.MouseEvent<HTMLButtonElement>): void => {
                                    e.preventDefault()
                                    setAnchorEl(e.currentTarget)
                                }}
                            />
                        )
                    }
                </CampaignEditorInputFieldContainer>
                <StyledRichContentMenu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={() => setAnchorEl(null)}
                    isVertical={false}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    sx={{ marginTop: '-10px' }}
                >
                    {requirementList.map((list: { title: string; requirements: RequirementProps[] }, index: number) => {
                        return (
                            <div key={index} className="menu-section">
                                <MenuItem.Title text={list.title} size="s" case="uppercase" />
                                <>
                                    {list.requirements.map((requirement: RequirementProps) => {
                                        return (
                                            <MenuItem.Img
                                                key={requirement.type}
                                                title={requirement.label}
                                                onClick={(): void => handleAddBlock(requirement.initialValue)}
                                                description={requirement.description.replace(/(\r\n|\n|\r)/gm, '')}
                                                img={getRequirementIcon(requirement.type)}
                                                isDisabled={values.requirementsData.some((selected: Requirement) =>
                                                    _.isEqual(getRequirementType(selected), requirement.type),
                                                )}
                                            />
                                        )
                                    })}
                                </>
                            </div>
                        )
                    })}
                </StyledRichContentMenu>
            </RequirementContainer>
        </InputFieldContainer>
    )
}

export default CampaignRequirements
